/**
 * Adapter between the conditional editor's flat operator UI and the
 * BoolExpr proto schema.
 */
import {
  BoolExpr,
  ComparisonOp,
  TimeRangeOp,
  VariableRef,
} from '@/proto/logiblocks/logiblocks';
import { VariableType } from '@/proto/common';
import { COMPARISON_OP_LABELS, TIME_RANGE_OP_LABELS } from '../../enumData';
import type { FieldItem } from '@/apis/logiblocksAPI';

/**
 * UI-level operator for a leaf boolean expression.
 */
export type ExpressionOp =
  | { kind: 'comparison'; op: ComparisonOp }
  | { kind: 'recurringWindow'; op: TimeRangeOp };

/** Canonical labels used in the prefetched-fields operator dropdown. */
const PREFETCH_OP_LABELS: Partial<Record<ComparisonOp, string>> = {
  [ComparisonOp.COMPARISON_EQUALS]: 'Is',
  [ComparisonOp.COMPARISON_NOT_EQUALS]: 'Is not',
  [ComparisonOp.COMPARISON_EXISTS]: 'Exists',
  [ComparisonOp.COMPARISON_NOT_EXISTS]: 'Does not exist',
  [ComparisonOp.COMPARISON_CONTAINS]: 'Contains',
  [ComparisonOp.COMPARISON_NOT_CONTAINS]: 'Does not contain',
  [ComparisonOp.COMPARISON_GREATER_THAN]: 'Greater than',
  [ComparisonOp.COMPARISON_LESS_THAN]: 'Less than',
  [ComparisonOp.COMPARISON_BEFORE]: 'Before',
  [ComparisonOp.COMPARISON_AFTER]: 'After',
  [ComparisonOp.COMPARISON_BETWEEN]: 'Between',
  [ComparisonOp.COMPARISON_NOT_BETWEEN]: 'Not between',
};

/**
 * Returns the label for an ExpressionOp.
 * Canonical catalog labels take priority; falls back to legacy proto labels.
 */
export const expressionOpLabel = (op: ExpressionOp): string => {
  switch (op.kind) {
    case 'comparison':
      return PREFETCH_OP_LABELS[op.op] ?? COMPARISON_OP_LABELS[op.op];
    case 'recurringWindow':
      return TIME_RANGE_OP_LABELS[op.op];
  }
};

export const getExprLhs = (expr: BoolExpr): VariableRef | undefined =>
  expr.comparison_expr?.variable ??
  expr.date_time_set_expr?.variable ??
  expr.recurring_window_expr?.variable;

export const setExprLhs = (
  expr: BoolExpr,
  lhs: VariableRef | undefined
): BoolExpr => {
  if (expr.comparison_expr)
    return { comparison_expr: { ...expr.comparison_expr, variable: lhs } };
  if (expr.date_time_set_expr)
    return {
      date_time_set_expr: { ...expr.date_time_set_expr, variable: lhs },
    };
  if (expr.recurring_window_expr)
    return {
      recurring_window_expr: { ...expr.recurring_window_expr, variable: lhs },
    };
  throw new Error('setExprLhs: BoolExpr has no leaf branch set');
};

export const getExprOp = (expr: BoolExpr): ExpressionOp | undefined => {
  if (expr.comparison_expr) {
    if ((expr.comparison_expr.operator as number) < 0) return undefined;
    return { kind: 'comparison', op: expr.comparison_expr.operator };
  }
  // Within / Not Within lives on a date_time_set_expr or a legacy
  // recurring_window_expr.
  if (expr.date_time_set_expr)
    return { kind: 'recurringWindow', op: expr.date_time_set_expr.operator };
  if (expr.recurring_window_expr)
    return { kind: 'recurringWindow', op: expr.recurring_window_expr.operator };
  return undefined;
};

export const setExprOp = (expr: BoolExpr, op: ExpressionOp): BoolExpr => {
  const lhs = getExprLhs(expr);
  switch (op.kind) {
    case 'comparison':
      return {
        comparison_expr: {
          variable: lhs,
          operator: op.op,
          rhs: undefined,
          rhs2: undefined,
        },
      };
    case 'recurringWindow':
      // Keep the existing leaf's preset/list/entries and flip its operator;
      // a leaf from another operator family starts empty.
      if (expr.date_time_set_expr)
        return {
          date_time_set_expr: { ...expr.date_time_set_expr, operator: op.op },
        };
      if (expr.recurring_window_expr)
        return {
          recurring_window_expr: {
            ...expr.recurring_window_expr,
            operator: op.op,
          },
        };
      return {
        date_time_set_expr: {
          variable: lhs,
          iana_timezone: '',
          operator: op.op,
          display_label: '',
        },
      };
  }
};

/**
 * Operator list for graph variables (LHS comes from the variable picker, not prefetchedFields).
 */
export const availableOps = (
  lhsType: VariableType | undefined
): ExpressionOp[] => {
  const cmp = (op: ComparisonOp): ExpressionOp => ({ kind: 'comparison', op });
  const rw = (op: TimeRangeOp): ExpressionOp => ({
    kind: 'recurringWindow',
    op,
  });

  switch (lhsType) {
    case VariableType.STRING:
    case VariableType.TEXT:
      return [
        cmp(ComparisonOp.COMPARISON_EQUALS),
        cmp(ComparisonOp.COMPARISON_NOT_EQUALS),
        cmp(ComparisonOp.COMPARISON_CONTAINS),
        cmp(ComparisonOp.COMPARISON_NOT_CONTAINS),
        cmp(ComparisonOp.COMPARISON_STARTS_WITH),
        cmp(ComparisonOp.COMPARISON_ENDS_WITH),
        cmp(ComparisonOp.COMPARISON_IS_EMPTY),
        cmp(ComparisonOp.COMPARISON_IS_NOT_EMPTY),
        cmp(ComparisonOp.COMPARISON_REGEX_MATCHES),
        cmp(ComparisonOp.COMPARISON_EXISTS),
        cmp(ComparisonOp.COMPARISON_NOT_EXISTS),
      ];
    case VariableType.NUMBER:
      return [
        cmp(ComparisonOp.COMPARISON_EQUALS),
        cmp(ComparisonOp.COMPARISON_NOT_EQUALS),
        cmp(ComparisonOp.COMPARISON_GREATER_THAN),
        cmp(ComparisonOp.COMPARISON_GREATER_THAN_OR_EQUAL),
        cmp(ComparisonOp.COMPARISON_LESS_THAN),
        cmp(ComparisonOp.COMPARISON_LESS_THAN_OR_EQUAL),
        cmp(ComparisonOp.COMPARISON_BETWEEN),
        cmp(ComparisonOp.COMPARISON_NOT_BETWEEN),
        cmp(ComparisonOp.COMPARISON_EXISTS),
        cmp(ComparisonOp.COMPARISON_NOT_EXISTS),
      ];
    case VariableType.BOOL:
      return [
        cmp(ComparisonOp.COMPARISON_IS_TRUE),
        cmp(ComparisonOp.COMPARISON_IS_FALSE),
        cmp(ComparisonOp.COMPARISON_EQUALS),
        cmp(ComparisonOp.COMPARISON_NOT_EQUALS),
        cmp(ComparisonOp.COMPARISON_EXISTS),
        cmp(ComparisonOp.COMPARISON_NOT_EXISTS),
      ];
    case VariableType.DATE_TIME:
      return [
        cmp(ComparisonOp.COMPARISON_BEFORE),
        cmp(ComparisonOp.COMPARISON_ON_OR_BEFORE),
        cmp(ComparisonOp.COMPARISON_AFTER),
        cmp(ComparisonOp.COMPARISON_ON_OR_AFTER),
        cmp(ComparisonOp.COMPARISON_EXISTS),
        cmp(ComparisonOp.COMPARISON_NOT_EXISTS),
        rw(TimeRangeOp.WITHIN),
        rw(TimeRangeOp.NOT_WITHIN),
      ];
    case VariableType.DATE_TIME_RANGE:
      return [
        cmp(ComparisonOp.COMPARISON_BEFORE),
        cmp(ComparisonOp.COMPARISON_ON_OR_BEFORE),
        cmp(ComparisonOp.COMPARISON_AFTER),
        cmp(ComparisonOp.COMPARISON_ON_OR_AFTER),
        cmp(ComparisonOp.COMPARISON_EXISTS),
        cmp(ComparisonOp.COMPARISON_NOT_EXISTS),
        rw(TimeRangeOp.WITHIN),
        rw(TimeRangeOp.NOT_WITHIN),
      ];
    case VariableType.ALERT_TYPE:
      return [
        cmp(ComparisonOp.COMPARISON_EQUALS),
        cmp(ComparisonOp.COMPARISON_NOT_EQUALS),
        cmp(ComparisonOp.COMPARISON_IN_LIST),
        cmp(ComparisonOp.COMPARISON_NOT_IN_LIST),
        cmp(ComparisonOp.COMPARISON_EXISTS),
        cmp(ComparisonOp.COMPARISON_NOT_EXISTS),
      ];
    case VariableType.SHIPMENT:
    case VariableType.ALERT:
      return [
        cmp(ComparisonOp.COMPARISON_EQUALS),
        cmp(ComparisonOp.COMPARISON_NOT_EQUALS),
        cmp(ComparisonOp.COMPARISON_EXISTS),
        cmp(ComparisonOp.COMPARISON_NOT_EXISTS),
      ];
    default:
      return [
        cmp(ComparisonOp.COMPARISON_EQUALS),
        cmp(ComparisonOp.COMPARISON_NOT_EQUALS),
        cmp(ComparisonOp.COMPARISON_EXISTS),
        cmp(ComparisonOp.COMPARISON_NOT_EXISTS),
      ];
  }
};

/**
 * `isEnum` STRING/TEXT fields whose "enum" values are actually free text a
 * company defines for itself (e.g. custom location categories, custom
 * transport-provider service levels) rather than a fixed, Python-computed
 * value set. These fields get the same text-search operators a free-text
 * field offers, in addition to Is/Is not. Every other `isEnum` field
 * (severity, pod_status, alert_stage, alert_state, timeliness, …) is a
 * genuinely closed set and keeps the narrower Is/Is not/Exists/Does not
 * exist list.
 */
const COMPANY_DEFINED_ENUM_FIELDS = new Set<string>([
  'destination_location_type',
  'origin_location_type',
  'transport_provider_service_level',
]);

/**
 * Whether a field is one of COMPANY_DEFINED_ENUM_FIELDS. At runtime these are
 * derived variables read off a meta-variable (see metaVariableCatalog.ts), so
 * the name to match is derived_field_name. field_name holds whatever name the
 * publishing node gave its output variable, which varies per workflow.
 */
const isCompanyDefinedEnumField = (field: FieldItem): boolean =>
  COMPANY_DEFINED_ENUM_FIELDS.has(field.derived_field_name ?? field.field_name);

/** Operators that need a free-text RHS (a search pattern), not a value dropdown. */
const FREE_TEXT_ENUM_OPS = new Set<ComparisonOp>([
  ComparisonOp.COMPARISON_CONTAINS,
  ComparisonOp.COMPARISON_NOT_CONTAINS,
  ComparisonOp.COMPARISON_STARTS_WITH,
  ComparisonOp.COMPARISON_ENDS_WITH,
  ComparisonOp.COMPARISON_REGEX_MATCHES,
]);

/**
 * Whether `op` on `field` needs a free-text RHS input (a search pattern)
 * rather than the field's dropdown of currently-existing values. True only
 * for the text-search operators on a company-defined-enum field — see
 * COMPANY_DEFINED_ENUM_FIELDS above. Is/Is not on those same fields keep the
 * dropdown-of-existing-values RHS.
 */
export const needsFreeTextRhs = (
  field: FieldItem | null | undefined,
  op: ExpressionOp | undefined
): boolean => {
  if (!field?.is_enum || !op || op.kind !== 'comparison') return false;
  if (!isCompanyDefinedEnumField(field)) return false;
  return FREE_TEXT_ENUM_OPS.has(op.op);
};

/**
 * Operators offered for a conditional-node field, keyed on its type, plus (for
 * strings) whether the field is multi-select or a fixed-option enum.
 *
 *   STRING multi-select        → Exists, Does not exist, Contains, Does not contain
 *   STRING enum (company-defined) → Is, Is not, Exists, Does not exist, Contains, Does not contain, Starts with, Ends with, Matches regex
 *   STRING enum (closed)       → Is, Is not, Exists, Does not exist
 *   STRING free text           → Is, Is not, Exists, Does not exist, Contains, Does not contain
 *   NUMBER                     → Is, Is not, Exists, Does not exist, Greater than, Less than
 *   DATE_TIME                  → Is, Is not, Exists, Does not exist, Before, After, Within, Not within
 *   BOOL / ALERT_TYPE          → Is, Is not, Exists, Does not exist
 *   TRANSPORT_PROVIDER         → Is, Is not, Exists, Does not exist, Contains, Does not contain
 *   other                      → Is, Is not, Exists, Does not exist
 */
export const availableOpsForField = (field: FieldItem): ExpressionOp[] => {
  if (!field) return [];
  const cmp = (op: ComparisonOp): ExpressionOp => ({ kind: 'comparison', op });
  const rw = (op: TimeRangeOp): ExpressionOp => ({
    kind: 'recurringWindow',
    op,
  });

  const IS = cmp(ComparisonOp.COMPARISON_EQUALS);
  const IS_NOT = cmp(ComparisonOp.COMPARISON_NOT_EQUALS);
  const EXISTS = cmp(ComparisonOp.COMPARISON_EXISTS);
  const NOT_EXISTS = cmp(ComparisonOp.COMPARISON_NOT_EXISTS);
  const CONTAINS = cmp(ComparisonOp.COMPARISON_CONTAINS);
  const NOT_CONTAINS = cmp(ComparisonOp.COMPARISON_NOT_CONTAINS);
  const STARTS_WITH = cmp(ComparisonOp.COMPARISON_STARTS_WITH);
  const ENDS_WITH = cmp(ComparisonOp.COMPARISON_ENDS_WITH);
  const REGEX_MATCHES = cmp(ComparisonOp.COMPARISON_REGEX_MATCHES);
  const GT = cmp(ComparisonOp.COMPARISON_GREATER_THAN);
  const LT = cmp(ComparisonOp.COMPARISON_LESS_THAN);
  const BEFORE = cmp(ComparisonOp.COMPARISON_BEFORE);
  const AFTER = cmp(ComparisonOp.COMPARISON_AFTER);
  const WITHIN = rw(TimeRangeOp.WITHIN);
  const NOT_WITHIN = rw(TimeRangeOp.NOT_WITHIN);

  const type = field.type as VariableType;
  const isMulti = field.is_multi_select;
  const isEnum = field.is_enum ?? false;

  switch (type) {
    case VariableType.STRING:
    case VariableType.TEXT:
      if (isMulti) return [EXISTS, NOT_EXISTS, CONTAINS, NOT_CONTAINS];
      if (isEnum) {
        if (isCompanyDefinedEnumField(field)) {
          return [
            IS,
            IS_NOT,
            EXISTS,
            NOT_EXISTS,
            CONTAINS,
            NOT_CONTAINS,
            STARTS_WITH,
            ENDS_WITH,
            REGEX_MATCHES,
          ];
        }
        return [IS, IS_NOT, EXISTS, NOT_EXISTS];
      }
      return [IS, IS_NOT, EXISTS, NOT_EXISTS, CONTAINS, NOT_CONTAINS];

    case VariableType.NUMBER:
      return [IS, IS_NOT, EXISTS, NOT_EXISTS, GT, LT];

    case VariableType.DATE_TIME:
      return [
        EXISTS,
        NOT_EXISTS,
        BEFORE,
        cmp(ComparisonOp.COMPARISON_ON_OR_BEFORE),
        AFTER,
        cmp(ComparisonOp.COMPARISON_ON_OR_AFTER),
        WITHIN,
        NOT_WITHIN,
      ];

    case VariableType.DATE_TIME_RANGE:
      return [
        EXISTS,
        NOT_EXISTS,
        BEFORE,
        cmp(ComparisonOp.COMPARISON_ON_OR_BEFORE),
        AFTER,
        cmp(ComparisonOp.COMPARISON_ON_OR_AFTER),
        WITHIN,
        NOT_WITHIN,
      ];

    case VariableType.BOOL:
      return [IS, IS_NOT, EXISTS, NOT_EXISTS];

    case VariableType.ALERT_TYPE:
      return [IS, IS_NOT, EXISTS, NOT_EXISTS];
    case VariableType.TRANSPORT_PROVIDER:
      return [IS, IS_NOT, EXISTS, NOT_EXISTS, CONTAINS, NOT_CONTAINS];

    default:
      return [IS, IS_NOT, EXISTS, NOT_EXISTS];
  }
};

export const encodeOp = (op: ExpressionOp): string => `${op.kind}:${op.op}`;

export const decodeOp = (s: string): ExpressionOp => {
  const [kind, opStr] = s.split(':');
  const op = parseInt(opStr, 10);
  switch (kind) {
    case 'comparison':
      return { kind: 'comparison', op: op as ComparisonOp };
    case 'recurringWindow':
      return { kind: 'recurringWindow', op: op as TimeRangeOp };
    default:
      throw new Error(`decodeOp: unrecognized ExpressionOp kind "${kind}"`);
  }
};
